// Copyright (c) 2023 embyt GmbH. All rights reserved.
// Author: Roman Morawek <roman.morawek@embyt.com>

import { ITariffCircuit, IEnergyMeter, EnergySource } from "shared/types/parameter";
import { ITrendData, ITrendDataRow } from "states/trendingstates";
import { checkCategories, getColor, roundTimestamp } from "guiutils";
import { isSet } from "shared/utils/utils";
import { HierarchySelectionState, ITreeViewDropDownOption } from "view/comp/TreeViewDropDown";
import { Category } from "states/cachedstates";

const DEFAULT_ENERGY_SOURCE = "electrical";

class TariffCircuitList {
  public readonly tariffCircuits;
  public readonly energyMeters;
  private readonly activeParentIds: Set<number>;

  constructor(tariffCircuits: ITariffCircuit[], energyMeters: IEnergyMeter[]) {
    this.tariffCircuits = tariffCircuits;
    this.energyMeters = energyMeters;
    this.activeParentIds = new Set(
      tariffCircuits
        .filter((tc) => tc.webActive && isSet(tc.parent))
        .map((tc) => tc.parent as number),
    );
  }

  // first energy meter of a circuit, descending into contained circuits
  private getFirstEnergyMeterId(tc: ITariffCircuit, path: number[] = []): number | undefined {
    for (const curContr of tc.contributions ?? []) {
      if (isSet(curContr.em)) {
        return curContr.em;
      }
      const child = this.tariffCircuits.find((cur, index) => (cur.id ?? index) === curContr.tc);
      if (child && !path.includes(curContr.tc!)) {
        const em = this.getFirstEnergyMeterId(child, [...path, curContr.tc!]);
        if (isSet(em)) return em;
      }
    }
    return undefined;
  }

  private getEnergyMeter(tlIndex: number) {
    const emId = this.getFirstEnergyMeterId(this.tariffCircuits[tlIndex]);
    return this.energyMeters.find((curItem) => curItem.id === emId);
  }

  public getEnergySources(allowAllSources: boolean) {
    const sourcesList = this.tariffCircuits
      .filter((curItem) => curItem.webActive)
      .map(
        (curItem) =>
          this.energyMeters.find((curEm) => curEm.id === this.getFirstEnergyMeterId(curItem))
            ?.energySource ?? DEFAULT_ENERGY_SOURCE,
      );
    const sources = Array.from(new Set(sourcesList));
    return allowAllSources ? [undefined, ...sources] : sources;
  }

  public getAvailableEnergySources() {
    const sourcesList = this.tariffCircuits
      .filter((curItem) => curItem.webActive)
      .map(
        (curItem) =>
          this.energyMeters.find((curEm) => curEm.id === this.getFirstEnergyMeterId(curItem))
            ?.energySource ?? DEFAULT_ENERGY_SOURCE,
      );
    const sources = Array.from(new Set(sourcesList));
    return sources;
  }

  public getCategories(inEnergySource: EnergySource | undefined) {
    const categories: Category[] = [];

    const allTariffCircuitIndexes = this.getAllChildHierarchies(
      inEnergySource,
      undefined,
      [],
      undefined,
    );

    allTariffCircuitIndexes
      .filter(
        (curItem) =>
          isSet(curItem.referenceEntry?.categories) &&
          ((curItem.referenceEntry?.categories.length ?? 0) > 1 ||
            curItem.referenceEntry?.categories[0] !== ""),
      )
      .every((curItem) =>
        curItem.referenceEntry?.categories?.every((cat: Category) => categories.push(cat)),
      );

    const sortedCategories = Array.from(new Set(categories)).sort((a, b) =>
      isSet(a) && isSet(b) ? a.localeCompare(b)
      : isSet(a) ? 1
      : -1,
    );

    return [
      undefined, // all categories
      ...sortedCategories,
      null, // unmapped categories
    ];
  }

  public getTariffCircuits(
    selectedEnergySource: EnergySource | undefined,
    selectedHierarchyId?: number | null | undefined,
  ) {
    // check if it is allowed to select all energy sources
    const energySource =
      selectedEnergySource ?? this.energyMeters[0]?.energySource ?? DEFAULT_ENERGY_SOURCE;
    // get list of used tariff circuits
    const allList = Array.from(Array(this.tariffCircuits.length).keys());

    const filteredList = allList.filter((curIndex) => {
      const isIdInAnyParent = this.hasIdInAnyParent(
        selectedHierarchyId,
        this.tariffCircuits[curIndex],
      );
      const showTariffCircuit =
        this.tariffCircuits[curIndex].webActive &&
        (!isSet(energySource) ||
          (this.getEnergyMeter(curIndex)?.energySource ?? DEFAULT_ENERGY_SOURCE) ===
            energySource) &&
        ((!isSet(selectedHierarchyId) && isIdInAnyParent) ||
          this.tariffCircuits[curIndex].parent === selectedHierarchyId ||
          (this.tariffCircuits[curIndex].parent === null && selectedHierarchyId === undefined) ||
          this.tariffCircuits[curIndex].id === selectedHierarchyId);

      return showTariffCircuit;
    });

    const sortedList = filteredList.sort((a, b) =>
      this.tariffCircuits[a].name.localeCompare(this.tariffCircuits[b].name),
    );

    if (isSet(selectedHierarchyId)) {
      const rootIndex = sortedList.findIndex(
        (entry) =>
          isSet(selectedHierarchyId) &&
          selectedHierarchyId === (this.tariffCircuits[entry].id ?? entry),
      );
      if (rootIndex !== -1) {
        const selectedHierarchyIdx = this.tariffCircuits.findIndex(
          (entry) => selectedHierarchyId === (entry.id ?? entry),
        );
        sortedList.splice(rootIndex, 1);
        return [selectedHierarchyIdx, ...sortedList];
      }
    }
    return sortedList;
  }

  public getTariffCircuitsWithCategory(inCategory: Category | undefined) {
    const selectedTariffs =
      inCategory === undefined ?
        this.tariffCircuits
      : this.tariffCircuits.filter((tc) =>
          inCategory === null ?
            !isSet(tc.categories) || tc.categories.includes("")
          : tc.categories?.includes(inCategory),
        );

    const retval = selectedTariffs.filter((tc) => isSet(tc.id)).map((tc) => tc.id!);
    return retval;
  }

  public isChildOfParent(entryId: number[], parentEntryId: number[]): boolean {
    if (entryId.length <= parentEntryId.length) {
      return false;
    }
    for (let i = 0; i < parentEntryId.length; i++) {
      if (entryId[i] !== parentEntryId[i]) {
        return false;
      }
    }
    return true;
  }

  public getSelectedEnergySources(energySource?: string) {
    // for now -> only one energy-source is supported
    // if no energy-source is given, use the DEFAULT_ENERGY_SOURCE
    // in future, if we support multiple energy-sources, we need to
    // change [DEFAULT_ENERGY_SOURCE] to this.getEnergySources(false)
    return isSet(energySource) ? [energySource] : [DEFAULT_ENERGY_SOURCE];
  }

  public makeOptionsArray(
    inEnergySource?: string,
    inSelectedCategory?: Category,
  ): ITreeViewDropDownOption[] {
    const optionsArray: ITreeViewDropDownOption[] = [];
    // for now -> only one energy-source is supported
    // if no energy-source is given, use the DEFAULT_ENERGY_SOURCE
    // in future, if we support multiple energy-sources, we need to
    // change [DEFAULT_ENERGY_SOURCE] to this.getEnergySources(false)
    const energySources = this.getSelectedEnergySources(inEnergySource);
    const energySourcesAvailable = this.getAvailableEnergySources().length;

    energySources.forEach((curSource, indexSource) => {
      const entryId = [indexSource * -1 - 1]; // use negative IDs for sources to avoid conflicts with tariff-circuit-ids
      optionsArray.push({
        checked: HierarchySelectionState.UNSELECTED,
        text: energySourcesAvailable === 1 ? this.getEnergySourceName(curSource) : "Root",
        color: getColor(indexSource),
        isEndNode: energySources.length === 1,
        referenceEntry: undefined,
        hasChildren: true,
        entryId,
      } as ITreeViewDropDownOption);
      optionsArray.push(
        // add all tariff circuits of this source as children
        ...this.getAllChildHierarchies(curSource, undefined, entryId, inSelectedCategory),
      );
    });
    // return the tree structure without selection; selection is applied in applySelection
    return optionsArray;
  }

  public applySelection(
    inStructure: ITreeViewDropDownOption[],
    inSelectedTariffs: number[] | undefined,
    inHierarchySelection: boolean,
  ): ITreeViewDropDownOption[] {
    const optionsArray = inStructure.map((item) => ({ ...item }));
    const selectedSet = new Set(inSelectedTariffs);

    const sourceIndices = optionsArray
      .filter((e) => e.entryId.length === 1)
      .map((e) => e.entryId[0]);

    sourceIndices.forEach((indexSource) => {
      const sourceOptions = optionsArray.filter((entry) => entry.entryId[0] === indexSource);
      const selectedTariffs = sourceOptions.filter(
        (entry) =>
          !isSet(inSelectedTariffs) ||
          selectedSet.has(entry.referenceEntry?.id ?? entry.entryId.at(-1) ?? -1),
      );

      // depending on the selected hierarchy-mode select or child-select entries
      if (inHierarchySelection === true) {
        // unselect all entries first
        sourceOptions.forEach((option) => (option.checked = HierarchySelectionState.UNSELECTED));

        // set first selected entry as selected (root)
        // find the first entry with "no parent"....
        const found = inSelectedTariffs?.[0];
        let selectedTariff: ITreeViewDropDownOption | undefined;

        // check if anything is selected
        if (isSet(found)) {
          const rootEntry = sourceOptions.find(
            (entry) => (entry.referenceEntry?.id ?? entry.entryId.at(-1)) === found,
          );
          if (isSet(rootEntry)) {
            const anyWithParent = selectedTariffs.some((entry) =>
              isSet(entry.referenceEntry?.parent),
            );
            selectedTariff = anyWithParent === false ? sourceOptions[0] : rootEntry;
          }
        } else {
          const firstWithChildren = sourceOptions.find(
            (entry) =>
              entry.hasChildren === true &&
              entry.entryId.length === 2 &&
              entry.entryId[0] === indexSource,
          );
          const childrenOfRoot = sourceOptions.filter(
            // check if sourceOptions[0] has more than one child...
            (entry) => entry.entryId.length === 2 && entry.entryId[0] === indexSource,
          );
          selectedTariff =
            isSet(firstWithChildren) && childrenOfRoot.length === 1 ?
              firstWithChildren
            : sourceOptions[0];
        }

        if (isSet(selectedTariff)) {
          selectedTariff.checked = HierarchySelectionState.SELECTED;
          sourceOptions.forEach((option) => {
            if (
              option.entryId.length === selectedTariff!.entryId.length + 1 &&
              this.isChildOfParent(option.entryId, selectedTariff!.entryId)
            ) {
              option.checked = HierarchySelectionState.SELECTED_CHILD;
            }
          });
        }
      } else {
        // single-selection mode...
        // select all tariffs now
        selectedTariffs.forEach((option) => {
          option.checked = HierarchySelectionState.SELECTED;
        });
      }
    });

    // loop only thru entries which have children (roots) which are selected/child-selected
    // this loop makes sure, that if any sub-enty is selected a dot is shown (SOMESELECTED)
    // or if all sub-entries of a source-root (electirc, water, oid, ....) is selected that
    //  SELECTED is shown!
    // if no sub-entry is selected, an empty box is shown
    optionsArray
      .filter(
        (entry) =>
          entry.hasChildren === true &&
          entry.checked !== HierarchySelectionState.SELECTED &&
          entry.checked !== HierarchySelectionState.SELECTED_CHILD,
      )
      .forEach((option) => {
        const subLevelEntries = optionsArray.filter(
          (item) =>
            this.isChildOfParent(item.entryId, option.entryId) &&
            ((inHierarchySelection === false && isSet(option.referenceEntry)) ||
              item.entryId !== option.entryId),
        );
        const checkedEntries = subLevelEntries.reduce(
          (prev, cur) => prev + (cur.checked === HierarchySelectionState.SELECTED ? 1 : 0),
          0,
        );
        if (checkedEntries === 0) {
          // no sub-entries are checked
          option.checked = HierarchySelectionState.UNSELECTED;
        } else if (
          checkedEntries === subLevelEntries.length &&
          (option.referenceEntry === undefined || inHierarchySelection === true)
        ) {
          // all sub-entries are checked
          option.checked = HierarchySelectionState.SELECTED;
        } else {
          // some entries are checked
          option.checked = HierarchySelectionState.SOMESELECTED;
        }
      });

    return optionsArray;
  }

  public updateNormalSelection(
    inOptionsArray: ITreeViewDropDownOption[],
    inSelectedTariffs?: number[],
  ): number[] | undefined {
    // get (first) selected tariff
    const firstSelected = inOptionsArray.find(
      (entry) => entry.checked === HierarchySelectionState.SELECTED,
    );
    const parentTariffId = firstSelected?.referenceEntry?.id ?? firstSelected?.entryId.at(-1);

    if (inSelectedTariffs === undefined || inSelectedTariffs.length === 0) {
      // if no tariffs are selected, return the ids of selected child entries that reference a parent tariff
      return inOptionsArray
        .filter(
          (entry) =>
            entry.checked === HierarchySelectionState.SELECTED_CHILD &&
            entry.referenceEntry !== undefined,
        )
        .map((entry) => entry.referenceEntry?.id ?? entry.entryId.at(-1) ?? -1);
    }

    return isSet(parentTariffId) ?
        inSelectedTariffs?.filter((entry) => entry !== parentTariffId)
      : inSelectedTariffs;
  }

  public updateHierarchySelection(
    inOptionsArray: ITreeViewDropDownOption[],
    inSelectedTariffs?: number[],
    inSelectedCategory?: Category,
  ): number[] | undefined {
    // get the first entry with "no parent".... (selectedTariffs is always sorted like the dropdown itself!)
    // this method needs rework if we move all energy-sources with their tariffs into this one treeview - currently
    // only one energy-source is available at once within the treeview -> so using the first is sufficient
    const found =
      inSelectedCategory !== undefined ?
        ((inOptionsArray
          .find(
            (item) =>
              (item.checked === HierarchySelectionState.SELECTED ||
                item.checked === HierarchySelectionState.SELECTED_CHILD) &&
              checkCategories(inSelectedCategory, item.referenceEntry?.categories),
          )
          ?.entryId.at(-1) as number) ?? inSelectedTariffs?.[0])
      : inSelectedTariffs?.[0];

    // disable all selected entries
    inOptionsArray.forEach((entry) => (entry.checked = HierarchySelectionState.UNSELECTED));

    // check if anything is selected
    if (isSet(found)) {
      //find the entry-id which is selected
      const foundEntryId = inOptionsArray.find(
        (entry) => (entry.referenceEntry?.id ?? entry.entryId.at(-1)) === found,
      )?.entryId;

      // check if we found an entry-id
      if (isSet(foundEntryId)) {
        // find parent-entry of this found entry
        const parentEntryId = foundEntryId.slice(0, -1);

        // search in Array only for entry-ids with the same depth (array-size) and then if all entries are equal
        const parententry = inOptionsArray.find(
          (entry) =>
            parentEntryId.length === entry.entryId.length &&
            parentEntryId.every((subid, index) => entry.entryId[index] === subid),
        );

        // check if a parent was found
        if (isSet(parententry)) {
          // if so -> select it
          parententry.checked = HierarchySelectionState.SELECTED;

          // filter childs of found entry (next level only -> so depth with one ore than the current array-size)
          // but all starting entries of the arrays must be equal
          const allSelectedOptions = inOptionsArray.filter((entry) => {
            return (
              parententry.entryId.length + 1 === entry.entryId.length &&
              parententry.entryId.every((subid, index) => entry.entryId[index] === subid)
            );
          });

          // check if any of this newly selected options does have a parent...
          const anyWithParent = allSelectedOptions.some((entry) =>
            isSet(entry.referenceEntry?.parent),
          );

          // if none have a parent, we need to select the engery-source (OptionsArray[0] for now -> remember: only one energy-source is within the treeview)
          // as root and all others as SELECTED_CHILD
          if (anyWithParent === false) {
            parententry.checked = HierarchySelectionState.SELECTED_CHILD;
            inOptionsArray[0].checked = HierarchySelectionState.SELECTED;
          }

          // so al other selected Options as CHILD
          allSelectedOptions.forEach(
            (entry) => (entry.checked = HierarchySelectionState.SELECTED_CHILD),
          );
        }
      }
    }

    // loop thru the whole new created optionsArray (or if nothing was found the currently available optionsArray)
    // and map only the SELECTED and SELECTED_CHILD entries, tariff-id's
    const newSelectedTariffs = inOptionsArray
      .filter(
        (entry) =>
          isSet(entry.referenceEntry) &&
          (entry.checked === HierarchySelectionState.SELECTED ||
            entry.checked === HierarchySelectionState.SELECTED_CHILD),
      )
      .map((entry) => entry.referenceEntry?.id ?? entry.entryId.at(-1) ?? -1);

    return newSelectedTariffs;
  }

  private getAllChildHierarchies(
    inCurSource: EnergySource | undefined,
    inCurHierarchy: number | null | undefined,
    inCurEntryId: number[],
    inSelectedCategory: Category | undefined,
  ) {
    const optionsArray: ITreeViewDropDownOption[] = [];

    if (inCurHierarchy !== null) {
      const tariffCircuitIndexes = this.getTariffCircuits(inCurSource, inCurHierarchy).filter(
        (curItem) => {
          return (
            !isSet(inCurHierarchy) ||
            (this.tariffCircuits[curItem].id ?? curItem) !== inCurHierarchy
          );
        },
      );

      tariffCircuitIndexes.forEach((curTariffIndex, indexItem) => {
        const curTariff = this.tariffCircuits[curTariffIndex];
        const curTariffId = curTariff.id ?? curTariffIndex;
        const entryId = [...inCurEntryId, curTariffId];

        // check if the current hierarchy is parent of someone..
        const isParent = this.activeParentIds.has(curTariffId);

        if (isParent) {
          // hierarchy goes on!
          optionsArray.push({
            checked: HierarchySelectionState.UNSELECTED,
            text: curTariff.name,
            color:
              checkCategories(inSelectedCategory, curTariff.categories) ?
                getColor(curTariffId, false)
              : getColor(curTariffId, true),
            isEndNode: tariffCircuitIndexes.length === 1,
            referenceEntry: curTariff,
            hasChildren: true,
            entryId,
          } as ITreeViewDropDownOption);

          optionsArray.push(
            ...this.getAllChildHierarchies(inCurSource, curTariffId, entryId, inSelectedCategory),
          );
        } else {
          // hierarchy ends here!
          optionsArray.push({
            checked: HierarchySelectionState.UNSELECTED,
            text: curTariff.name,
            color:
              checkCategories(inSelectedCategory, curTariff.categories) ?
                getColor(curTariffId, false)
              : getColor(curTariffId, true),
            isEndNode: tariffCircuitIndexes.length === indexItem + 1,
            referenceEntry: curTariff,
            hasChildren: false,
            entryId,
          } as ITreeViewDropDownOption);
        }
      });
    }

    return optionsArray;
  }

  private getEnergySourceName(source: EnergySource | undefined) {
    if (source === "electrical" || !isSet(source)) return "Electrical";
    if (source === "water") return "Water";
    if (source === "gas") return "Gas";
    return source;
  }

  private hasIdInAnyParent(id: number | null | undefined, tc: ITariffCircuit): boolean {
    const tcIndex = this.tariffCircuits.indexOf(tc);
    if ((tc.id ?? tcIndex) === id) {
      return true;
    } else if (isSet(tc.parent)) {
      const aNode = this.tariffCircuits.find((item) => item.id === tc.parent);
      if (isSet(aNode)) {
        return this.hasIdInAnyParent(id, aNode);
      }
    }
    return false;
  }

  public getSelectedTimestampValues(
    trendData: ITrendData,
    tlId: number,
    trendMember: "energy" | "power" | "costs",
    usedTimestamps: number[],
    selectedTimestamp: number | undefined,
    trendDataIndex?: Map<string, ITrendDataRow>,
  ) {
    let maxMaxValue = 0;
    let maxTargetValue = 0;
    let maxValue = 0;
    let selectedValue = undefined;
    let sumOfValues = 0;

    // loop through timestamps for current tariff to find max-values and sum of all values
    usedTimestamps.forEach((curTimestamp) => {
      const matchRow =
        trendDataIndex ?
          trendDataIndex.get(`${tlId}:${curTimestamp}`)
        : trendData.find((r) => roundTimestamp(r.timestamp) === curTimestamp && r.tlId === tlId);
      const trendMaxValue = matchRow?.[trendMember] ?? 0;
      // calculate the max value of the selected time-period (only needed for cost and energy)
      sumOfValues += trendMaxValue;
      // store the maximum power (of the selected period)
      if (Math.abs(trendMaxValue) > Math.abs(maxMaxValue)) {
        maxMaxValue = trendMaxValue;
      }

      // get the target-value
      const trendTargetValue = matchRow?.targetpower ?? 0;
      // store the maximum target-value (of the selected period)
      if (Math.abs(trendTargetValue) > Math.abs(maxTargetValue)) {
        maxTargetValue = trendTargetValue;
      }

      // get the value
      const trendValue = matchRow?.[trendMember] ?? 0;
      if (Math.abs(trendValue) > Math.abs(maxValue)) {
        maxValue = trendValue;
      }
      // if only one timestamp is selected -> check if the curTimestamp is the one we are searching for
      // -> if so, store that value!
      if (isSet(selectedTimestamp) && selectedTimestamp === curTimestamp) {
        selectedValue = trendValue;
      }
    });

    // get max-value -> if a target-value was set, use this as max, otherwise the max power or energy-value
    const maxRelateValue =
      maxTargetValue > 0 ? maxTargetValue
      : maxMaxValue > 0 ? maxMaxValue
      : maxValue;

    return { selectedValue, sumOfValues, maxTargetValue, maxRelateValue };
  }

  public static hasCategories(inTariffLines: ITariffCircuit[]): boolean {
    return inTariffLines.some(
      (curItem: ITariffCircuit) =>
        isSet(curItem.categories) &&
        curItem.categories.length > 0 &&
        curItem.categories.some((curCategory) => curCategory !== ""),
    );
  }

  public static hasHierarchy(inTariffLines: ITariffCircuit[]): boolean {
    return inTariffLines.some((curItem) => isSet(curItem.parent));
  }
}

export default TariffCircuitList;
